import type { ListItem } from "../../components/List/List"
import { OrderEntity } from "../Order/OrderEntity"
import { MemoryClass } from "./MemoryClass"
import { convertToLocalMoney } from "../Generics/Helps"
import { ECustomerPaymentType } from "../../Enum/ECustomerPaymentType"
import i18next from "i18next"
import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { getOrderFlowStatusBadges } from "../Order/orderStatusBadges"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBoxOpen,
  faClock,
  faFileInvoiceDollar,
  faLocationDot,
  faMoneyBillWave,
  faMotorcycle,
  faReceipt,
  faTableCells,
  faTruckFast,
  faUser,
} from "@fortawesome/free-solid-svg-icons"

const renderProviderChargeReference = (externalReference?: string) => {
  if (!externalReference) {
    return null
  }

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 flex flex-col gap-1">
      <span className="font-semibold flex items-center gap-2">
        <FontAwesomeIcon icon={faMoneyBillWave} className="text-sky-600" />

        {i18next.t("order:label_provider_external_reference")}
      </span>
      <span className="break-all font-mono">{externalReference}</span>
    </div>
  )
}

export class OrderMemory extends MemoryClass<OrderEntity> {
  setList(items: OrderEntity[]): void {
    this.listData = items
  }

  protected cloneEntity(item: OrderEntity): OrderEntity {
    return new OrderEntity({ ...item })
  }

  getListPaidCards(id: number) {
    const cards: ListItem[] = []

    let count = 0

    this.listData.forEach((ss) => {
      if (ss.id != id && ss.orderPaymentEntity.status === ECustomerPaymentType.WithInDebit) {
        cards.push(this.getCard(ss, count))
      }
    })

    count++

    return cards
  }

  getListCards(all: boolean) {
    const cards: ListItem[] = []

    let count = 0

    this.listData.forEach((ss) => {
      if (all || ss.orderPaymentEntity.status === ECustomerPaymentType.WithInDebit) {
        cards.push(this.getCard(ss, count))
      }
    })

    count++

    return cards
  }

  getCard(ss: OrderEntity, count: number): ListItem {
    const isCanceled = ss.isCancel

    const isClosed = ss.orderPaymentEntity.status === ECustomerPaymentType.WithOutDebit

    const isOpen = ss.orderPaymentEntity.status === ECustomerPaymentType.WithInDebit

    const payment = ss.orderPaymentEntity

    const statusLabel = isCanceled
      ? i18next.t("order:status_canceled")
      : isClosed
        ? i18next.t("order:status_paid")
        : i18next.t("order:status_in_debit")

    const statusColor = isCanceled
      ? "bg-red-100 text-red-700"
      : isClosed
        ? "bg-green-100 text-green-700"
        : "bg-amber-100 text-amber-700"

    const flowStatusBadges = getOrderFlowStatusBadges(ss)

    const allItems = [...ss.orderProducts, ...ss.orderCatalogItems]

    const firstReservation = allItems.find((item) => item.isReservation)

    return {
      isDisable: !isOpen,
      id: ss.id,
      title: ss.getCustomerDisplayName() || i18next.t("order:label_order"),
      linkImage: "",
      index: count,
      dataFilter: [
        ss.getCustomerDisplayName(),
        ss.getCustomerDisplayCpf(),
        ss.customerEntity?.personEntity?.rg ?? "",
        ss.servicePointEntity?.name ?? "",
        payment.status.toString(),
        ...flowStatusBadges.map((badge) => badge.label),
        ...(ss.hasIssuedFiscalDocument ? [i18next.t("order:status_fiscal_document_issued")] : []),
      ],
      children: (
        <div
          className={`
      group
      flex flex-col gap-4
      p-4
      rounded-2xl
      border
      transition-all duration-300
      ${isCanceled ? "bg-red-50 border-red-200" : "bg-white border-orange-100 hover:shadow-lg hover:scale-[1.01]"}
    `}
        >
          <div className="flex justify-between items-start gap-3">
            <div className="flex flex-col gap-1">
              <span className="font-extrabold text-base text-gray-900 tracking-tight flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="text-orange-500" />
                {ss.getCustomerDisplayName()}
              </span>

              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mt-1">
                {ss.servicePointEntity && (
                  <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold">
                    <FontAwesomeIcon icon={faTableCells} />
                    {i18next.t("components:label_servicePoints")} {ss.servicePointEntity.name}
                  </span>
                )}

                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
                  <FontAwesomeIcon icon={faClock} className="text-slate-500" />
                  {ss.date.toReadableDate()}
                </span>

                {ss.eOrderPlaceType === EOrderPlaceType.Delivery && (
                  <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700 font-semibold">
                    <FontAwesomeIcon icon={faTruckFast} />
                    {ss.isStorePickup ? i18next.t("order:label_store_pickup") : i18next.t("order:title_popup_delivery")}
                  </span>
                )}
              </div>
            </div>

            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColor}`}>{statusLabel}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2 -mt-1">
            <span className="text-[11px] font-semibold text-gray-500">{i18next.t("order:status_flow_title")}</span>
            {flowStatusBadges.map((badge) => (
              <span
                key={`${ss.id}-${badge.label}`}
                className={`px-2 py-1 text-[11px] font-semibold rounded-full ${badge.className}`}
              >
                {badge.label}
              </span>
            ))}

            {ss.hasIssuedFiscalDocument && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                <FontAwesomeIcon icon={faFileInvoiceDollar} />

                {i18next.t("order:status_fiscal_document_issued")}
              </span>
            )}

            {firstReservation?.isReservation && (
              <span className="px-2 py-1 text-[11px] font-semibold rounded-full bg-violet-100 text-violet-700">
                📅 Reserva{" "}
                {firstReservation.reservationDate
                  ? `${firstReservation.reservationDate}`.toReadableDate("pt-BR", true)
                  : "-"}{" "}
                {firstReservation.reservationTime}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-700 font-medium">
              <FontAwesomeIcon icon={faBoxOpen} />
              <b>{ss.orderProducts.length}</b> {i18next.t("components:label_products")}
            </span>

            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-100 text-cyan-700 font-medium">
              <FontAwesomeIcon icon={faReceipt} />
              <b>{ss.orderCatalogItems.length}</b> {i18next.t("components:label_catalogItems")}
            </span>
          </div>

          {ss.eOrderPlaceType === EOrderPlaceType.Delivery && !ss.isStorePickup && ss.addressEntity && (
            <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900 flex items-start gap-2">
              <FontAwesomeIcon icon={faLocationDot} className="mt-0.5 text-sky-600" />

              <span>
                <span className="font-semibold">{i18next.t("order:label_address")}:</span> {ss.addressEntity.street},{" "}
                {ss.addressEntity.number} - {ss.addressEntity.neighborhood}
              </span>
            </div>
          )}

          {ss.eOrderPlaceType === EOrderPlaceType.Delivery && ss.isStorePickup && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex items-start gap-2">
              <FontAwesomeIcon icon={faLocationDot} className="mt-0.5 text-emerald-700" />

              <span>
                <span className="font-semibold">{i18next.t("order:label_store_pickup")}:</span>{" "}
                {i18next.t("order:label_pickup_at_store")}
              </span>
            </div>
          )}

          {payment.isOnlinePayment && renderProviderChargeReference(payment.externalReference)}

          {ss.deliveryEntity && (
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faMotorcycle} className="text-purple-700" />
                <span>
                  <span className="font-semibold">{i18next.t("order:label_courier")}:</span>{" "}
                  {ss.deliveryEntity.courierEntity?.personEntity?.name ?? i18next.t("order:label_waiting_link")}
                </span>
              </div>

              <div>
                <span className="font-semibold">{i18next.t("order:label_delivery_status")}:</span>{" "}
                {(ss.deliveryEntity.status as number).toDeliveryStatusTypeString()}
              </div>

              {ss.deliveryEntity.courierEntity && (
                <div>
                  <span className="font-semibold">{i18next.t("order:label_courier_status")}:</span>{" "}
                  {(ss.deliveryEntity.courierEntity.status as number).toCourierStatusTypeString()}
                </div>
              )}
            </div>
          )}

          <div className="border-t border-orange-100 pt-3 flex flex-col gap-1 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>{i18next.t("order:label_gross_total")}</span>
              <span>{convertToLocalMoney(payment.totalNotLiquid)}</span>
            </div>

            {payment.totalDiscount > 0 && (
              <div className="flex justify-between text-green-600 font-semibold">
                <span>{i18next.t("order:label_discount")}</span>
                <span>- {convertToLocalMoney(payment.totalDiscount)}</span>
              </div>
            )}

            <div className="flex justify-between text-gray-500">
              <span>{i18next.t("order:label_subtotal")}</span>
              <span>{convertToLocalMoney(payment.totalBase)}</span>
            </div>

            {payment.totalExtra > 0 && (
              <div className="flex justify-between text-red-600 font-semibold">
                <span>{i18next.t("order:label_services")}</span>
                <span>+ {convertToLocalMoney(payment.totalExtra)}</span>
              </div>
            )}

            {ss.eOrderPlaceType === EOrderPlaceType.Delivery && !ss.isStorePickup && (
              <div className="flex justify-between text-purple-700 font-semibold">
                <span>Frete</span>
                <span>{payment.shippingFee === 0 ? "Grátis" : convertToLocalMoney(payment.shippingFee)}</span>
              </div>
            )}

            {payment.totalAward > 0 && (
              <div className="flex justify-between text-green-600 font-semibold">
                <span>{i18next.t("order:label_awards")}</span>
                <span>- {convertToLocalMoney(payment.totalAward)}</span>
              </div>
            )}

            <div className="flex justify-between text-lg font-bold text-gray-900 mt-2 pt-2 border-t border-orange-100">
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faReceipt} className="text-orange-500" />
                {i18next.t("order:label_total_final")}
              </span>
              <span className="text-emerald-600">{convertToLocalMoney(payment.totalFinal)}</span>
            </div>
          </div>
        </div>
      ),
    }
  }

  protected getItemId(item: OrderEntity): number {
    return item.id
  }
}
